import type { BenefitPlanGroup } from "../types/models";
import type { Rate, RateSchedule } from "../types";
import type { EnrollmentMeasure } from "./quotedEnrollmentReview";

/** How serious a rate's enrollment mismatch is: the group's total, or the one rate. */
export type EnrollmentFlagLevel = "group" | "rate";

/** What decides the flags on a plan's enrolled cells. */
export interface EnrollmentFlagState {
  /** The group's total differs from Current's by 9% or more. */
  readonly groupFlagged: boolean;
  /** The analyst still owes a decision on this plan. */
  readonly needsDecision: boolean;
}

export const NO_ENROLLMENT_FLAGS: EnrollmentFlagState = {
  groupFlagged: false,
  needsDecision: false,
};

/**
 * The flag state of one plan. The group's flag and the schedules' decision state are the
 * server's, as the last Quotes read or the last rate save reported them. A rate save reports
 * the group's flag on the schedules it returns, and that is newer than the group's.
 */
export const enrollmentFlagStateOf = (
  group: BenefitPlanGroup,
  planSchedules: readonly RateSchedule[],
): EnrollmentFlagState => ({
  groupFlagged:
    planSchedules.find(
      (schedule) => schedule.enrollmentGroupFlagged !== undefined,
    )?.enrollmentGroupFlagged ?? group.enrollmentGroupFlagged === true,
  needsDecision: planSchedules.some(
    (schedule) => schedule.enrollmentNeedsDecision === true,
  ),
});

/** Whether the carrier quoted the figure a cell of this measure shows. */
const quotedMeasure = (rate: Rate, measure: EnrollmentMeasure): boolean =>
  measure === "volume"
    ? rate.carrierVolume !== undefined
    : rate.carrierNumberOfLives !== undefined;

/**
 * The flag a rate's enrolled cell shows. A rate flags while the analyst still owes a
 * decision, and only on the cell that shows a figure the carrier quoted. The group's total
 * is the more serious mismatch, so it takes the level over the rate's own.
 */
export const enrollmentFlagLevel = (
  state: EnrollmentFlagState,
  rate: Rate,
  measure: EnrollmentMeasure,
): EnrollmentFlagLevel | undefined => {
  if (!state.needsDecision || !quotedMeasure(rate, measure)) {
    return undefined;
  }

  if (state.groupFlagged) {
    return "group";
  }

  return rate.enrollmentVarianceFlagged ? "rate" : undefined;
};
